import React, { useState, useMemo, useEffect } from 'react';
import { find, isEmpty } from 'lodash';
import { Box } from '@availity/element';
import { useEffectAsync } from '@availity/hooks';

import { useAppContext } from '@/context';
import { checkNavigation, JOURNEY_REGISTER } from '@/global';
import { useFRStep } from '@/hooks';
import {
  NewUserClickToVerify,
  NewUserCompleted,
  NewUserConfirmation,
  NewUserEnrollment,
  NewUserEnrollmentInvitation,
  NewUserSecurityQuestions,
  NewUserVerified,
} from '@/pages';
import { queryByInviteIdCloud } from '@/api';

const Registration = () => {
  const {
    setLoading,
    securityQuestionsOptions,
    setSecurityQuestionsOptions,
    queryParams = {},
    addMessage,
  } = useAppContext();
  const [currentStep, setCurrentStep] = useState();
  const [formValues, setFormValues] = useState({
    newUserEnrollment: {
      firstName: '',
      lastName: '',
      email: '',
      userId: '',
      password: '',
      region: '',
    },
    securityQuestions: {
      IDToken1question: '',
      IDToken1answer: '',
      IDToken2question: '',
      IDToken2answer: '',
      IDToken3question: '',
      IDToken3answer: '',
    },
  });
  const [userInvitationDetails, setUserInvitationDetails] = useState({}); // for invited users

  const { authIndexValue = '', invitationGUID } = queryParams;

  // process authIndexValue in event that the UI should navigate to a different journey
  const journeyId = useMemo(() => authIndexValue?.toLowerCase(), [authIndexValue]);

  // call utility function to check if we need to navigate to a different route
  useEffect(() => {
    if (journeyId) {
      checkNavigation(journeyId);
    }
  }, [journeyId]);

  // handle getting invitation details if needed
  useEffectAsync(async () => {
    if (invitationGUID && isEmpty(userInvitationDetails)) {
      setLoading(true);

      try {
        const userInvitationResponse = await queryByInviteIdCloud(invitationGUID);

        setUserInvitationDetails(userInvitationResponse || {});
        setLoading(false);
      } catch (error) {
        if (error) {
          const { response = {} } = error;
          const { data = {} } = response;
          const { message } = data;

          // handle specific error scenarios for the invitations
          let errorToDisplay =
            'Unable to retrieve user details from invitation. Click on the link in your email to try again.';
          if (message?.indexOf('expired') >= 0) {
            errorToDisplay = 'This invitation has expired. Contact your administrator for a new link.';
          } else if (message?.indexOf('already been used') >= 0) {
            errorToDisplay = 'Looks like you have an Availity account. Click "Log In" to continue.';
          }

          addMessage(errorToDisplay);
          setLoading(false);
        }
      }
    }
  }, [invitationGUID]);

  // get FR step info from custom hook
  // NOTE: updating the currentStep 'should' trigger fetching of the stepToRender
  // currentStep should be changed on form submission
  const { stepToRender = {}, errorCode } = useFRStep(currentStep, JOURNEY_REGISTER);

  const pageNodeToRender = useMemo(() => {
    let component;

    if (stepToRender) {
      const { payload = {} } = stepToRender;
      const { callbacks = [], stage } = payload;

      if (callbacks[0]?.type?.toLowerCase() === 'suspendedtextoutputcallback') {
        // check for suspended text output callback first before checking stage values.
        component = <NewUserCompleted key="newUserCompleted" stepToRender={stepToRender} />;
      } else if (callbacks[0]?.type?.toLowerCase() === 'kbacreatecallback' && !securityQuestionsOptions) {
        // extract security questions options

        const { output = [] } = callbacks[0];
        const outputQuestionObj = find(output, (entry) => entry.name === 'predefinedQuestions', {});
        const { value: optionsArray } = outputQuestionObj;

        // build out options for select field
        const optionsToUse = [];
        for (const question of optionsArray) {
          optionsToUse.push({
            label: question,
            value: question,
          });
        }

        setSecurityQuestionsOptions([...optionsToUse]);

        component = (
          <NewUserSecurityQuestions
            key="newUserSecurityQuestions"
            stepToRender={stepToRender}
            setCurrentStep={setCurrentStep}
            formValues={formValues}
            setFormValues={setFormValues}
          />
        );
      } else {
        switch (stage) {
          case 'UserReg_Start': {
            component =
              invitationGUID && !isEmpty(userInvitationDetails) ? (
                <NewUserEnrollmentInvitation
                  key="newUserEnrollmentInvitation"
                  stepToRender={stepToRender}
                  setCurrentStep={setCurrentStep}
                  errorCode={errorCode}
                  userInvitationDetails={userInvitationDetails}
                  formValues={formValues}
                  setFormValues={setFormValues}
                />
              ) : (
                <NewUserEnrollment
                  key="newUserEnrollment"
                  stepToRender={stepToRender}
                  setCurrentStep={setCurrentStep}
                  errorCode={errorCode}
                  formValues={formValues}
                  setFormValues={setFormValues}
                />
              );
            break;
          }
          case 'UserReg_KBA': {
            component = (
              <NewUserSecurityQuestions
                key="newUserSecurityQuestions"
                stepToRender={stepToRender}
                setCurrentStep={setCurrentStep}
                formValues={formValues}
                setFormValues={setFormValues}
              />
            );
            break;
          }
          case 'UserReg_Confirmation': {
            component = (
              <NewUserConfirmation
                key="newUserConfirmation"
                stepToRender={stepToRender}
                setCurrentStep={setCurrentStep}
                formValues={formValues}
                userInvitationDetails={userInvitationDetails}
              />
            );
            break;
          }
          case 'UserReg_ChangeEmail': {
            component = <NewUserCompleted key="newUserCompleted" stepToRender={stepToRender} />;
            break;
          }
          case 'UserReg_Verify': {
            component = (
              <NewUserClickToVerify
                key="newUserClickToVerify"
                stepToRender={stepToRender}
                setCurrentStep={setCurrentStep}
              />
            );
            break;
          }
          case 'UserReg_Complete': {
            component = <NewUserVerified key="newUserVerified" />;
            break;
          }
          default:
        }
      }
    }

    return component;
  }, [stepToRender, invitationGUID, userInvitationDetails]);

  return <Box role="main">{pageNodeToRender}</Box>;
};

export default Registration;
